import jsPDF from 'jspdf';
import { BasePDFOptions } from '../index';
import { ForeeTransaction } from '@/types/pos/foree-report';
import { displayDate, formatToPKR, capitalizeFirstLetter } from '@/lib/helper';

export async function foreeReportTemplate(
  options: BasePDFOptions<ForeeTransaction[]>
): Promise<void> {
  const { 
    data, 
    filename = 'foree_report.pdf', 
    title = 'Foree Report', 
    dateRange,
    orientation = 'portrait',
    summary
  } = options;

  const pdf = new jsPDF(orientation === 'landscape' ? 'l' : 'p', 'mm', 'a4');
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 10;
  let yPosition = margin;

  // Header
  pdf.setFillColor(30, 41, 59);
  pdf.rect(0, 0, pageWidth, 25, 'F');
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(20);
  pdf.setFont('helvetica', 'bold');
  pdf.text(title, margin, 12);

  pdf.setFontSize(8);
  pdf.setFont('helvetica', 'normal');
  const currentDate = new Date();
  const formattedDate = currentDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  const formattedTime = currentDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  pdf.text(`Generated: ${formattedDate} at ${formattedTime}`, margin, 19);

  if (dateRange?.start_date || dateRange?.end_date) {
    const rangeText = `Period: ${dateRange.start_date || 'N/A'} to ${dateRange.end_date || 'N/A'}`;
    pdf.text(rangeText, margin + 60, 19);
  }

  const totalRecordsText = `Total Records: ${summary?.totalCount || data.length}`;
  pdf.text(totalRecordsText, pageWidth - margin, 19, { align: 'right' });

  yPosition = 35;

  // Table header
  pdf.setTextColor(15, 23, 42);
  const headers = ['TXN No', 'SRB No', 'Member', 'Method', 'Settlement Date', 'Amount', 'Status'];
  const tableWidth = pageWidth - (margin * 2);
  const colWidths = [
    tableWidth * 0.12, // TXN No
    tableWidth * 0.15, // SRB No
    tableWidth * 0.20, // Member
    tableWidth * 0.11, // Method
    tableWidth * 0.15, // Settlement Date
    tableWidth * 0.15, // Amount
    tableWidth * 0.12  // Status
  ];
  const tableStartX = margin;

  pdf.setFillColor(241, 245, 249);
  pdf.rect(tableStartX, yPosition, pageWidth - (margin * 2), 8, 'F');
  pdf.setDrawColor(203, 213, 225);
  pdf.setLineWidth(0.5);
  pdf.line(tableStartX, yPosition + 8, tableStartX + (pageWidth - (margin * 2)), yPosition + 8);

  pdf.setTextColor(51, 65, 85);
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'bold');
  
  let xPosition = tableStartX;
  headers.forEach((header, index) => {
    const padding = 2;
    if (index === 5) { // Amount right aligned (now index 5 in 7-column layout)
       pdf.text(header, xPosition + colWidths[index] - padding, yPosition + 5, { align: 'right' });
    } else {
       pdf.text(header, xPosition + padding, yPosition + 5);
    }
    xPosition += colWidths[index];
  });

  yPosition += 8;

  // Rows
  pdf.setFontSize(8);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(15, 23, 42);

  data.forEach((item, rowIndex) => {
    const settlementDate = item.payments?.find(p => p.payment_method === 'foree')?.settlement_date;
    
    const rowValues = [
      item.receipt_number || 'N/A',
      item.srb_invoice_id || 'N/A',
      item.member_name || 'N/A',
      item.payments?.map(p => p.payment_method).join(', ') || 'N/A',
      settlementDate ? displayDate(settlementDate) : 'N/A',
      formatToPKR(item.total),
      capitalizeFirstLetter(item.payments?.find(p => p.payment_method === 'foree')?.payment_status || "N/A")
    ];

    // Split text into lines for each column to handle wrapping
    const padding = 2;
    const cellLines = rowValues.map((val, idx) => {
      const colWidth = colWidths[idx];
      const availableWidth = colWidth - (padding * 2);
      return pdf.splitTextToSize(val, availableWidth);
    });

    // Calculate dynamic row height based on the maximum number of lines in any cell
    const maxLineCount = Math.max(...cellLines.map(lines => lines.length));
    const lineHeight = 4.5; // mm
    const currentRowHeight = Math.max(8, (maxLineCount * lineHeight) + 2);

    // Page overflow check
    if (yPosition + currentRowHeight > pageHeight - 15) {
      pdf.addPage();
      yPosition = margin;
    }

    // Row background (stripe)
    if (rowIndex % 2 === 0) {
      pdf.setFillColor(249, 250, 251);
      pdf.rect(tableStartX, yPosition, pageWidth - (margin * 2), currentRowHeight, 'F');
    }

    xPosition = tableStartX;
    cellLines.forEach((lines: string[], cellIndex) => {
      const colWidth = colWidths[cellIndex];
      
      lines.forEach((line: string, lineIdx: number) => {
        const yOffset = 5 + (lineIdx * lineHeight);
        if (cellIndex === 5) { // Amount right aligned
          pdf.text(line, xPosition + colWidth - padding, yPosition + yOffset, { align: 'right' });
        } else {
          pdf.text(line, xPosition + padding, yPosition + yOffset);
        }
      });
      xPosition += colWidth;
    });

    // Bottom border for the row
    pdf.setDrawColor(226, 232, 240);
    pdf.setLineWidth(0.2);
    pdf.line(tableStartX, yPosition + currentRowHeight, tableStartX + (pageWidth - (margin * 2)), yPosition + currentRowHeight);
    
    yPosition += currentRowHeight;
  });

  pdf.save(filename);
}
